import React, { useMemo } from "react";
import { Container, Button } from "react-bootstrap";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { TruncatedText } from "../../../components/truncateText";
import { GlobalSpinner, PDFHeader, changeDateFormate } from "../../../../../global/globalFunctions";
import MaterialTable from "material-table";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import "./InventoryLedgerDetail.css";



const formatNumber = (val) => {
  const n = Number(val);
  if (isNaN(n)) return val ?? 0;
  return n.toLocaleString();
};
const InventoryLegderDetail = ({ searchInventoryLedgerList, isFetchingSearchInventoryLedger }) => {

  const summaryCards = useMemo(() => {
    const data = searchInventoryLedgerList || {};
    return [
      {
        label: "Product",
        value: `${data.code || "-"} • ${data.productName || "-"}`,
        accent: "accent-indigo",
        muted: true,
      },
      {
        label: "Total Add Quantity",
        value: formatNumber(data.addQuantity || 0),
        accent: "accent-green",
      },
      {
        label: "Total Less Quantity",
        value: formatNumber(data.lessQuantity || 0),
        accent: "accent-red",
      },
      {
        label: "Closing Balance",
        value: formatNumber(data.closing_balance || 0),
        accent: "accent-blue",
      },
      {
        label: "Period",
        value: `${changeDateFormate(data.startDate)} → ${changeDateFormate(data.endDate)}`,
        accent: "accent-amber",
        muted: true,
      },
    ];
  }, [searchInventoryLedgerList]);

  /* Table columns */
  const columns = useMemo(
    () => [
      {
        title: "Date",
        field: "transactionDate",
        render: (row) => (<span className="ild-date-cell"> {changeDateFormate(row.transactionDate)} </span>),
        width: 110,
      },
      {
        title: "Description",
        field: "description",
        render: (row) => (<TruncatedText text={row?.description} maxLength={80} />),
      },
      {
        title: "Add Qty",
        field: "addQuantity",
        render: (row) =>
          row.addQuantity ? (
            <span className="ild-badge badge-add"> + {formatNumber(row.addQuantity)} </span>) : (
            <span className="ild-empty-dash">—</span>
          ),
        width: 110,
      },
      {
        title: "Less Qty",
        field: "lessQuantity",
        render: (row) =>
          row.lessQuantity ? (
            <span className="ild-badge badge-less">  − {formatNumber(row.lessQuantity)} </span>) : (
            <span className="ild-empty-dash">—</span>
          ),
        width: 110,
      },
      {
        title: "Closing Balance",
        field: "closingBalance",
        render: (row) => (<span className="ild-badge badge-balance">  {formatNumber(row.closingBalance)}</span>),
        width: 140,
      },
    ],
    []
  );

  /* PDF export */
  const handleExportPdf = (cols, data) => {
    const doc = new jsPDF("l", "pt", "A4");
    PDFHeader(doc, "Store Ledger");

    const columnTitles = cols.map((c) => c.title);
    const pdfData = data.map((elt) => [
      changeDateFormate(elt.transactionDate),
      elt.description || "",
      elt.addQuantity || 0,
      elt.lessQuantity || 0,
      elt.closingBalance || 0,
    ]);

    if (!_.isEmpty(pdfData)) {
      pdfData.push([
        "",
        {
          content: "TOTAL",
          styles: { halign: "right", fontStyle: "bold" },
        },
        {
          content: parseFloat(_.sumBy(pdfData, "2").toFixed()),
          styles: { halign: "left", fontStyle: "bold" },
        },
        {
          content: parseFloat(_.sumBy(pdfData, "3").toFixed()),
          styles: { halign: "left", fontStyle: "bold" },
        },
        "",
      ]);
    }

    doc.setFontSize(12);
    doc.setFont("Times New Roman", "Bold");
    doc.text(`Start Date: ${changeDateFormate(searchInventoryLedgerList?.startDate)} - End Date: ${changeDateFormate(searchInventoryLedgerList?.endDate)}`, 50, 120);

    doc.autoTable({
      startY: 135,
      startX: 5,
      head: [columnTitles],
      body: pdfData,
      headStyles: { fillColor: "#2980ba", font: "Amiri", fontSize: 10 },
      styles: { fontSize: 9 },
      theme: "grid",
    });

    doc.setProperties({ title: "Store Ledger" });
    doc.output("dataurlnewwindow");
  };

  /* Table options */
  const tableOptions = {
    exportButton: true,
    toolbar: true,
    showTitle: false,
    actionsColumnIndex: -1,
    paging: true,
    pageSize: 50,
    pageSizeOptions: [50, 100, 150, 200],
    emptyRowsWhenPaging: false,
    search: true,
    searchFieldStyle: { fontSize: "13px" },
    headerStyle: {
      position: "sticky",
      top: 0,
      color: "#475467",
      fontWeight: 600,
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "10px 12px",
      backgroundColor: "#F9FAFB",
      borderBottom: "1px solid #EAECF0",
    },
    cellStyle: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "13px",
      padding: "10px 12px",
      borderBottom: "1px solid #F2F4F7",
      color: "#1F2A44",
    },
    rowStyle: (rowData, index) => ({
      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#FAFBFC",
    }),
  };

  if (isFetchingSearchInventoryLedger) {
    return <GlobalSpinner />;
  }

  return (
    <Container fluid className="ild-page">
      <div className="ild-topbar">
        <div>
          <h2 className="ild-title">Inventory Ledger</h2>
          <div className="ild-subtitle">
            Detailed transaction log for the selected product
          </div>
        </div>
        <Link to="/inventory/ledger" className="ild-back-link">
          <Button className="ild-back-btn">← BACK TO SEARCH</Button>
        </Link>
      </div>

      <div className="ild-summary-wrap">
        {summaryCards.map((card, idx) => (
          <div key={idx} className="ild-card">
            <div className={`ild-card-accent ${card.accent}`} />
            <div className="ild-card-label">{card.label}</div>
            <div className={card.muted ? "ild-card-value-muted" : "ild-card-value"} >
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="ild-table-wrap">
        <MaterialTable
          columns={columns}
          data={searchInventoryLedgerList?.data || []}
          options={{
            ...tableOptions,
            exportPdf: handleExportPdf,
          }}
          localization={{
            body: { emptyDataSourceMessage: "No transactions found for this period", },
            toolbar: { searchPlaceholder: "Search transactions..." },
            pagination: {
              labelRowsSelect: "rows",
              labelDisplayedRows: "{from}-{to} of {count}",
            },
          }}
        />
      </div>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  searchInventoryLedgerList: state.InventoryLegderReducer.searchInventoryLedgerList,
  isFetchingSearchInventoryLedger: state.InventoryLegderReducer.isFetchingSearchInventoryLedger,
});

export default connect(mapStateToProps)(InventoryLegderDetail);